import React, { useState } from 'react';
import styled from 'styled-components';
import { FiX, FiPlus, FiTrash2, FiSave, FiAlertCircle, FiCheck, FiEdit3 } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';
import GradebookItemAssigner from './GradebookItemAssigner';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Container = styled.div`
  background: var(--background-primary);
  border-radius: 16px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  background: var(--background-primary);
  z-index: 10;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
    letter-spacing: -0.02em;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 12px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: var(--text-primary);
    background: var(--background-secondary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

const Content = styled.div`
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color);
  letter-spacing: -0.01em;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 0.875rem;

  .required {
    color: var(--error-color);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  background: var(--background-primary);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.875rem;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  background: var(--background-primary);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.875rem;
  border-radius: 12px;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CategoryHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.25rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  margin-bottom: 0.5rem;
`;

const CategoryHeaderLabels = styled.div`
  flex: 1;
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
`;

const HeaderLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:first-child {
    flex: 2;
  }

  &:last-child {
    flex: 1;
  }
`;

const CategoryCard = styled.div`
  padding: 1.25rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-sm);

  &:hover {
    border-color: var(--primary-color);
    box-shadow: var(--shadow-md);
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
`;

const CategoryInputs = styled.div`
  flex: 1;
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const CategoryNameInput = styled(Input)`
  flex: 2;
`;

const CategoryWeightInput = styled(Input)`
  flex: 1;
  min-width: 80px;
`;

const DeleteButton = styled.button`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid var(--error-color);
  color: var(--error-color);
  padding: 0.625rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 12px;
  min-width: 40px;
  min-height: 40px;

  &:hover:not(:disabled) {
    background: var(--error-color);
    color: white;
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;


const AddCategoryButton = styled.button`
  background: transparent;
  border: 2px dashed var(--primary-color);
  color: var(--primary-color);
  padding: 1rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  border-radius: 12px;
  width: 100%;

  &:hover {
    background: rgba(216, 184, 137, 0.1);
    border-color: var(--primary-dark);
    color: var(--primary-dark);
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

const WeightDistribution = styled.div`
  padding: 1rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  margin-top: 1rem;

  p {
    margin: 0 0 0.5rem 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .weight-bar {
    display: flex;
    gap: 0.25rem;
    height: 24px;
    margin-top: 0.75rem;
    border-radius: 12px;
    overflow: hidden;
    background: var(--background-secondary);
  }

  .weight-segment {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    background: var(--primary-color);
    transition: all 0.2s ease;
  }
`;

const AlertBox = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  border: 1px solid;
  align-items: flex-start;

  &.error {
    background: rgba(244, 67, 54, 0.1);
    border-color: var(--error-color);
    color: var(--error-color);

    svg {
      color: var(--error-color);
      flex-shrink: 0;
      margin-top: 0.125rem;
    }
  }

  &.success {
    background: rgba(76, 175, 80, 0.1);
    border-color: var(--success-color);
    color: var(--success-color);

    svg {
      color: var(--success-color);
      flex-shrink: 0;
      margin-top: 0.125rem;
    }
  }

  &.warning {
    background: rgba(255, 193, 7, 0.1);
    border-color: var(--warning-color);
    color: var(--warning-color);

    svg {
      color: var(--warning-color);
      flex-shrink: 0;
      margin-top: 0.125rem;
    }
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.5;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
  margin-top: 2rem;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 0.75rem;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    box-shadow: var(--shadow-sm);

    &:hover:not(:disabled) {
      background: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }

  &.secondary {
    background: var(--background-primary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover:not(:disabled) {
      background: var(--background-secondary);
      border-color: var(--border-dark);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 1.25rem;
  background: var(--background-secondary);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);

  @media (max-width: 600px) {
    gap: 1rem;
    padding: 1rem;
  }
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  color: ${props => props.$active ? 'var(--primary-color)' : props.$completed ? 'var(--success-color)' : 'var(--text-secondary)'};
  cursor: ${props => props.$completed ? 'pointer' : 'default'};
  transition: all 0.2s ease;

  &:hover {
    ${props => props.$completed ? 'color: var(--primary-color);' : ''}
  }
`;

const StepNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$active ? 'var(--primary-color)' : props.$completed ? 'var(--success-color)' : 'var(--background-tertiary)'};
  color: ${props => props.$active || props.$completed ? 'white' : 'var(--text-primary)'};
  font-weight: 700;
  font-size: 0.9rem;
`;

const CategoryItemCard = styled.div`
  padding: 1.25rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    box-shadow: var(--shadow-md);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryItemInfo = styled.div`
  flex: 1;

  .category-name {
    font-weight: 600;
    font-size: 1rem;
    color: var(--text-primary);
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .category-weight {
    font-size: 0.875rem;
    color: var(--text-secondary);
    font-weight: 500;
  }

  .items-count {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-top: 0.25rem;
  }
`;

const ItemBadge = styled.span`
  display: inline-block;
  padding: 0.375rem 0.875rem;
  background: ${props => props.count > 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 193, 7, 0.1)'};
  color: ${props => props.count > 0 ? 'var(--success-dark)' : 'var(--warning-dark)'};
  border: 1px solid ${props => props.count > 0 ? 'var(--success-color)' : 'var(--warning-color)'};
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.75rem;
  white-space: nowrap;
`;

const AssignItemsButton = styled.button`
  padding: 0.625rem 1rem;
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  box-shadow: var(--shadow-sm);

  &:hover:not(:disabled) {
    background: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

/**
 * Component for building/creating grading rubrics (Percentage-Weighted System)
 * Allows instructors to define categories with percentage weights
 * Categories are linked to gradebook items (quizzes, assignments, attendance)
 */

// Default categories for new rubrics
const DEFAULT_CATEGORIES = [
  { id: 1, name: 'Assessments', weight_percentage: 35 },
  { id: 2, name: 'Attendance', weight_percentage: 15 },
  { id: 3, name: 'Midterm', weight_percentage: 25 },
  { id: 4, name: 'Final', weight_percentage: 25 }
];

function RubricBuilder({ courseId, onSave, onCancel, editingRubric = null }) {
  // Step management
  const [step, setStep] = useState(editingRubric ? 'items' : 'rubric');
  const [savedRubric, setSavedRubric] = useState(editingRubric || null);

  // Rubric creation form
  const [rubricName, setRubricName] = useState(editingRubric?.name || '');
  const [rubricDescription, setRubricDescription] = useState(editingRubric?.description || '');

  // Initialize categories with defaults for new rubrics, or existing for editing
  const getInitialCategories = () => {
    if (editingRubric?.categories && Array.isArray(editingRubric.categories)) {
      return editingRubric.categories;
    }
    return DEFAULT_CATEGORIES;
  };

  const [categories, setCategories] = useState(getInitialCategories());

  // Calculate next category ID from existing categories
  const getNextCategoryId = () => {
    if (categories.length === 0) return 1;
    return Math.max(...categories.map(c => c.id || 0)) + 1;
  };

  const [nextCategoryId, setNextCategoryId] = useState(getNextCategoryId());

  // Item assignment
  const [categoryItemsCount, setCategoryItemsCount] = useState({});
  const [selectedCategoryForAssignment, setSelectedCategoryForAssignment] = useState(null);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);

  // Status
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load category items when editing a rubric (one-time on mount)
  React.useEffect(() => {
    if (editingRubric && step === 'items' && Object.keys(categoryItemsCount).length === 0) {
      loadCategoryItemsCounts(editingRubric.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRubric?.id, step]);

  // Calculate total weight with 0.01% tolerance for floating point rounding
  const totalWeight = categories.reduce((sum, cat) => sum + (parseFloat(cat.weight_percentage) || 0), 0);
  const isWeightValid = Math.abs(totalWeight - 100) < 0.01 && categories.length > 0;

  const handleCategoryChange = (categoryId, field, value) => {
    setCategories(
      categories.map(cat =>
        cat.id === categoryId ? { ...cat, [field]: value } : cat
      )
    );
  };

  const addCategory = () => {
    setCategories([
      ...categories,
      {
        id: nextCategoryId,
        name: '',
        weight_percentage: 0
      }
    ]);
    setNextCategoryId(nextCategoryId + 1);
  };

  const removeCategory = (categoryId) => {
    if (categories.length > 1) {
      setCategories(categories.filter(cat => cat.id !== categoryId));
    }
  };

  const validateForm = () => {
    setError(null);

    if (!rubricName.trim()) {
      setError('Rubric name is required');
      return false;
    }

    if (categories.length === 0) {
      setError('At least one category is required');
      return false;
    }

    for (const cat of categories) {
      if (!cat.name.trim()) {
        setError('All categories must have a name');
        return false;
      }
      if (!cat.weight_percentage || cat.weight_percentage <= 0) {
        setError('All categories must have a weight greater than 0%');
        return false;
      }
    }

    if (!isWeightValid) {
      setError(`Category weights must sum to 100% (currently ${totalWeight.toFixed(2)}%)`);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const rubricData = {
        courseId,
        name: rubricName,
        description: rubricDescription,
        rubricType: 'analytical',
        categories: categories.map(cat => ({
          id: cat.id,
          name: cat.name,
          weight_percentage: parseFloat(cat.weight_percentage)
        }))
      };

      let response;
      if (editingRubric) {
        response = await apiService.updateRubric(editingRubric.id, rubricData);
      } else {
        response = await apiService.createRubric(rubricData);
      }

      if (response.success) {
        // Parse categories if they're a JSON string (from JSON_ARRAYAGG)
        let parsedCategories = response.data.categories;
        if (typeof parsedCategories === 'string') {
          try {
            parsedCategories = JSON.parse(parsedCategories);
          } catch (e) {
            logger.error('Failed to parse categories JSON', { e, categories: parsedCategories });
            parsedCategories = null;
          }
        }
        
        // Normalize category field names and ensure we have an array
        if (Array.isArray(parsedCategories) && parsedCategories.length > 0) {
          parsedCategories = parsedCategories.map(cat => ({
            id: cat.id,
            name: cat.name,
            description: cat.description || null,
            weight_percentage: cat.weight_percentage || cat.weightPercentage || 0,
            order_index: cat.order_index || cat.orderIndex || 0
          }));
        } else if (!parsedCategories || parsedCategories.length === 0) {
          // Fallback to local categories if API didn't return them
          // But we need to fetch the rubric again to get the database IDs
          logger.warn('API did not return categories, fetching rubric again');
          try {
            const rubricResponse = await apiService.getRubric(response.data.id);
            if (rubricResponse.success && rubricResponse.data) {
              let fetchedCategories = rubricResponse.data.categories;
              if (typeof fetchedCategories === 'string') {
                fetchedCategories = JSON.parse(fetchedCategories);
              }
              if (Array.isArray(fetchedCategories) && fetchedCategories.length > 0) {
                parsedCategories = fetchedCategories.map(cat => ({
                  id: cat.id,
                  name: cat.name,
                  description: cat.description || null,
                  weight_percentage: cat.weight_percentage || cat.weightPercentage || 0,
                  order_index: cat.order_index || cat.orderIndex || 0
                }));
              }
            }
          } catch (fetchError) {
            logger.error('Failed to fetch rubric after creation', { fetchError });
          }
        }
        
        // If we still don't have categories, use local categories as fallback
        // This shouldn't happen, but it's a safety net
        if (!parsedCategories || parsedCategories.length === 0) {
          logger.warn('No categories found in API response, using local categories');
          parsedCategories = categories;
        }
        
        // Ensure categories are included in savedRubric with correct database IDs
        const rubricWithCategories = {
          ...response.data,
          categories: parsedCategories
        };
        setSavedRubric(rubricWithCategories);
        setStep('items');
        // Use the categories from rubricWithCategories to ensure we have the correct database IDs
        await loadCategoryItemsCounts(response.data.id, rubricWithCategories.categories);
      } else {
        setError(response.message || 'Failed to save rubric');
      }
    } catch (err) {
      logger.error('Error saving rubric:', err);
      setError(err.message || 'An error occurred while saving the rubric');
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryItemsCounts = async (rubricId, categoriesToUse = null) => {
    try {
      const counts = {};
      // Use provided categories, or fall back to savedRubric categories, or local categories
      const categoriesToLoad = categoriesToUse || savedRubric?.categories || categories;
      
      // Log for debugging
      logger.debug('Loading category items counts', {
        rubricId,
        categoriesToLoadCount: categoriesToLoad?.length,
        categoriesToLoad: categoriesToLoad?.map(c => ({ id: c.id, name: c.name }))
      });
      
      // Ensure we have an array before iterating
      if (!Array.isArray(categoriesToLoad) || categoriesToLoad.length === 0) {
        logger.warn('No categories to load items for', { 
          categoriesToLoad, 
          savedRubric: savedRubric?.id,
          hasSavedRubricCategories: !!savedRubric?.categories
        });
        setCategoryItemsCount(counts);
        return;
      }
      
      for (const cat of categoriesToLoad) {
        // Ensure we have a valid category ID
        if (!cat.id) {
          logger.warn('Category missing ID, skipping item count fetch', { categoryName: cat.name });
          continue;
        }
        
        try {
          const resp = await apiService.request(
            `/rubrics/categories/${cat.id}/items`,
            { method: 'GET' }
          );
          counts[cat.id] = (resp.data || []).length;
        } catch (err) {
          // If we get a 403, it might mean the category doesn't exist yet (local ID)
          // or there's a permission issue. Log it but don't fail completely.
          if (err.status === 403) {
            logger.warn('403 error loading items for category - may be using local ID', { 
              categoryId: cat.id, 
              categoryName: cat.name,
              rubricId,
              hasSavedRubric: !!savedRubric
            });
          } else {
            logger.warn('Failed to load items for category', { 
              categoryId: cat.id, 
              categoryName: cat.name, 
              error: err.message,
              status: err.status
            });
          }
          counts[cat.id] = 0;
        }
      }
      setCategoryItemsCount(counts);
    } catch (err) {
      logger.error('Error loading category items:', err);
    }
  };

  const allCategoriesHaveItems = () => {
    const activeCategories = savedRubric?.categories || categories;
    // Ensure activeCategories is an array before calling .every()
    if (!Array.isArray(activeCategories)) {
      return false;
    }
    return activeCategories.every(cat => (categoryItemsCount[cat.id] || 0) > 0);
  };

  const handleApplyRubric = async () => {
    if (!allCategoriesHaveItems()) {
      setError('All categories must have at least one gradebook item assigned');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request(
        `/rubrics/${savedRubric.id}/apply`,
        { method: 'POST' }
      );

      if (response.success) {
        if (onSave) {
          onSave(response.data || savedRubric);
        }
        setError(null);
      } else {
        setError(response.message || 'Failed to apply rubric');
      }
    } catch (err) {
      logger.error('Error applying rubric:', err);
      setError(err.message || 'An error occurred while applying the rubric');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRubric = async () => {
    if (!editingRubric) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the rubric "${rubricName}"? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setLoading(true);
    try {
      const response = await apiService.request(
        `/rubrics/${editingRubric.id}`,
        { method: 'DELETE' }
      );

      if (response.success) {
        setError(null);
        if (onSave) {
          onSave(null); // Pass null to indicate deletion
        }
      } else {
        setError(response.message || 'Failed to delete rubric');
      }
    } catch (err) {
      logger.error('Error deleting rubric:', err);
      setError(err.message || 'An error occurred while deleting the rubric');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onCancel}>
      <Container onClick={e => e.stopPropagation()}>
        <Header>
          <h2>{editingRubric ? 'Edit Rubric' : 'Create New Rubric'}</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {editingRubric && (
              <CloseButton
                onClick={handleDeleteRubric}
                disabled={loading}
                title="Delete this rubric"
                style={{ color: 'var(--danger-color)' }}
              >
                <FiTrash2 size={20} />
              </CloseButton>
            )}
            <CloseButton onClick={onCancel} disabled={loading}>
              <FiX size={24} />
            </CloseButton>
          </div>
        </Header>

        <Content>
          {/* Step Indicator */}
          {!editingRubric && (
            <StepIndicator>
              <Step $active={step === 'rubric'} $completed={step === 'items'}>
                <StepNumber $active={step === 'rubric'} $completed={step === 'items'}>
                  {step === 'items' ? <FiCheck size={16} /> : '1'}
                </StepNumber>
                <span>Rubric Details</span>
              </Step>
              <Step $active={step === 'items'} $completed={false}>
                <StepNumber $active={step === 'items'}>2</StepNumber>
                <span>Assign Items</span>
              </Step>
            </StepIndicator>
          )}

          {error && (
            <AlertBox className="error">
              <FiAlertCircle size={20} />
              <span>{error}</span>
            </AlertBox>
          )}

          {/* Step 1: Rubric Details */}
          {step === 'rubric' && (
            <>
          <FormSection>
            <SectionTitle>Rubric Information</SectionTitle>
            <FormGroup>
              <Label>
                Rubric Name <span className="required">*</span>
              </Label>
              <Input
                type="text"
                value={rubricName}
                onChange={e => setRubricName(e.target.value)}
                placeholder="e.g., Essay Grading Rubric"
              />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <TextArea
                value={rubricDescription}
                onChange={e => setRubricDescription(e.target.value)}
                placeholder="Optional: Add any notes or instructions for this rubric..."
              />
            </FormGroup>
          </FormSection>

          <FormSection>
            <SectionTitle>Grading Categories</SectionTitle>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              Define categories with percentage weights. You'll assign gradebook items (quizzes, assignments, attendance) to each category after creation.
            </p>

            {totalWeight > 0 && (
              <WeightDistribution>
                <p>Weight Distribution:</p>
                <div className="weight-bar">
                  {categories
                    .filter(cat => cat.weight_percentage > 0)
                    .map(cat => (
                      <div
                        key={cat.id}
                        className="weight-segment"
                        style={{ width: `${cat.weight_percentage}%` }}
                        title={`${cat.name}: ${cat.weight_percentage}%`}
                      >
                        {cat.weight_percentage > 10 ? `${cat.weight_percentage}%` : ''}
                      </div>
                    ))}
                </div>
                {!isWeightValid && (
                  <p style={{ marginTop: '0.75rem', color: 'var(--warning-color)' }}>
                    ⚠️ Weights must sum to 100% (currently {totalWeight.toFixed(2)}%)
                  </p>
                )}
              </WeightDistribution>
            )}

            <CategoriesContainer style={{ marginTop: '1.5rem' }}>
              <CategoryHeaderRow>
                <CategoryHeaderLabels>
                  <HeaderLabel>Name</HeaderLabel>
                  <HeaderLabel>Percentage</HeaderLabel>
                </CategoryHeaderLabels>
                <div style={{ minWidth: '40px' }}></div>
              </CategoryHeaderRow>
              {categories.map((category, idx) => (
                <CategoryCard key={category.id}>
                  <CategoryHeader>
                    <CategoryInputs>
                      <CategoryNameInput
                        type="text"
                        value={category.name}
                        onChange={e =>
                          handleCategoryChange(category.id, 'name', e.target.value)
                        }
                        placeholder="Category name (e.g., Assessments, Attendance)"
                      />
                      <CategoryWeightInput
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={category.weight_percentage}
                        onChange={e =>
                          handleCategoryChange(category.id, 'weight_percentage', e.target.value)
                        }
                        placeholder="Weight %"
                        title="Weight as percentage (0-100%)"
                      />
                    </CategoryInputs>
                    <DeleteButton
                      onClick={() => removeCategory(category.id)}
                      disabled={categories.length === 1}
                      title={categories.length === 1 ? 'At least one category required' : 'Delete category'}
                    >
                      <FiTrash2 size={16} />
                    </DeleteButton>
                  </CategoryHeader>
                </CategoryCard>
              ))}

              <AddCategoryButton type="button" onClick={addCategory}>
                <FiPlus size={18} />
                Add Category
              </AddCategoryButton>
            </CategoriesContainer>
          </FormSection>

            <ActionButtons>
              <Button className="secondary" onClick={onCancel} disabled={loading}>
                <FiX size={16} />
                Cancel
              </Button>
              <Button
                className="primary"
                onClick={handleSave}
                disabled={loading || !isWeightValid}
                title={!isWeightValid ? 'Fix weight distribution before saving' : ''}
              >
                <FiSave size={16} />
                {loading ? 'Saving...' : 'Next: Assign Items'}
              </Button>
            </ActionButtons>
            </>
          )}

          {/* Step 2: Assign Gradebook Items */}
          {step === 'items' && (savedRubric || categories.length > 0) && (
            <>
              <FormSection>
                <SectionTitle>Assign Gradebook Items to Categories</SectionTitle>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                  Select at least one gradebook item (quiz, assignment, or attendance) for each category. Students' scores from these items will be used to calculate the final grade.
                </p>

                {!allCategoriesHaveItems() && (
                  <AlertBox className="warning">
                    <FiAlertCircle size={20} />
                    <span>⚠️ All categories must have at least one item assigned before applying the rubric</span>
                  </AlertBox>
                )}

                <CategoriesContainer>
                  {(() => {
                    const categoriesToRender = savedRubric?.categories || categories;
                    
                    // Log for debugging
                    if (!categoriesToRender || !Array.isArray(categoriesToRender) || categoriesToRender.length === 0) {
                      logger.warn('No categories to render', { 
                        savedRubric: savedRubric?.id, 
                        hasSavedRubric: !!savedRubric,
                        savedRubricCategories: savedRubric?.categories,
                        localCategories: categories
                      });
                    }
                    
                    return Array.isArray(categoriesToRender) && categoriesToRender.length > 0 ?
                      categoriesToRender.map(category => {
                        // Ensure we have the correct weight_percentage field
                        const weight = category.weight_percentage || category.weightPercentage || 0;
                        return (
                          <CategoryItemCard key={category.id}>
                            <CategoryItemInfo>
                              <div className="category-name">
                                {category.name}
                                <ItemBadge count={categoryItemsCount[category.id] || 0}>
                                  {categoryItemsCount[category.id] || 0} item{(categoryItemsCount[category.id] || 0) !== 1 ? 's' : ''}
                                </ItemBadge>
                              </div>
                              <div className="category-weight">Weight: {weight}%</div>
                            </CategoryItemInfo>
                            <AssignItemsButton
                              onClick={() => {
                                setSelectedCategoryForAssignment(category);
                                setAssignmentModalOpen(true);
                              }}
                            >
                              <FiEdit3 size={14} />
                              {categoryItemsCount[category.id] ? 'Edit' : 'Assign'}
                            </AssignItemsButton>
                          </CategoryItemCard>
                        );
                      }) : (
                        <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                          No categories found. Please go back and create categories first.
                        </div>
                      );
                  })()}
                </CategoriesContainer>
              </FormSection>

              <ActionButtons>
                <Button className="secondary" onClick={() => setStep('rubric')} disabled={loading}>
                  <FiX size={16} />
                  Back
                </Button>
                <Button
                  className="primary"
                  onClick={handleApplyRubric}
                  disabled={loading || !allCategoriesHaveItems()}
                  title={!allCategoriesHaveItems() ? 'All categories must have at least one item' : ''}
                >
                  <FiCheck size={16} />
                  {loading ? 'Saving...' : 'Save Rubric'}
                </Button>
              </ActionButtons>
            </>
          )}
        </Content>

        {/* Gradebook Item Assigner Modal */}
        {assignmentModalOpen && selectedCategoryForAssignment && (
          <GradebookItemAssigner
            categoryId={selectedCategoryForAssignment.id}
            categoryName={selectedCategoryForAssignment.name}
            rubricId={savedRubric?.id}
            courseId={courseId}
            onSave={async () => {
              setAssignmentModalOpen(false);
              await loadCategoryItemsCounts(savedRubric.id);
            }}
            onCancel={() => {
              setAssignmentModalOpen(false);
              setSelectedCategoryForAssignment(null);
            }}
          />
        )}
      </Container>
    </Overlay>
  );
}

export default RubricBuilder;
